import {
    Bell, UserPlus, GraduationCap, Users, Settings, Trophy,
    CalendarDays, FileText, ShieldCheck, Lightbulb, CheckSquare, Megaphone, Inbox,
} from 'lucide-react';
import { findPageMeta, muze, smiNaStranku } from './moduly';

/**
 * Notifikace — vzhled a cíl kliknutí podle `modul` a `data` notifikace.
 * Jediný zdroj pro zvoneček (NotifikaceDropdown), stránku Notifikace (Aktivita)
 * i toasty v app.jsx.
 *
 * Cíl kliknutí posílá backend v `data.cil` (DruhNotifikace::cil, spočítaný pro
 * příjemce). CILE níž jsou jen pro starší notifikace bez něj.
 */

// Moduly s vlastní barvou notifikací. Ostatní přebírají ikonu a barvu sekce
// z katalogu modulů (moduly.js), takže nový modul nespadne do „Jiné".
const VLASTNI_VZHLED = {
    onboarding:  { label: 'Onboarding',  color: '#B47AEA', Icon: UserPlus },
    skoleni:     { label: 'Školení',     color: '#0CC0DF', Icon: GraduationCap },
    zamestnanci: { label: 'Zaměstnanci', color: '#FF8A00', Icon: Users },
    system:      { label: 'Systém',      color: '#6B7280', Icon: Settings },
    hodnoceni:   { label: 'Hodnocení',   color: '#10B981', Icon: Trophy },
    dokumenty:   { label: 'Dokumenty',   color: '#F59E0B', Icon: FileText },
    smeny:       { label: 'Směny',       color: '#3B82F6', Icon: CalendarDays },
    smernice:    { label: 'Směrnice',    color: '#EF4444', Icon: ShieldCheck },
    podnety:     { label: 'Podněty',     color: '#B47AEA', Icon: Lightbulb },
    // Komunikace (2026-09-29)
    ukoly:       { label: 'Úkoly',       color: '#B47AEA', Icon: CheckSquare },
    nastenka:    { label: 'Nástěnka',    color: '#B47AEA', Icon: Megaphone },
    zadosti:     { label: 'Žádosti',     color: '#B47AEA', Icon: Inbox },
};

// Moduly notifikací, které v katalogu modulů patří pod jiný modul
const ALIASY = {
    pokladna: { modul: 'finance', label: 'Pokladna' },
    // Flotila se 2026-10-10 přejmenovala na Rozvoz — starší notifikace
    flotila: { modul: 'rozvoz', label: 'Rozvoz' },
};

const JINE = { label: 'Jiné', color: '#8B82A6', bg: 'rgba(139,130,166,0.12)', Icon: Bell };

/** `{ label, color, bg, Icon }` pro modul notifikace. */
export function vzhledModulu(modul) {
    const vlastni = VLASTNI_VZHLED[modul];
    if (vlastni) return { ...vlastni, bg: `${vlastni.color}1f` };

    const alias = ALIASY[modul];
    const meta = findPageMeta(alias?.modul ?? modul);
    if (meta) return { label: alias?.label ?? meta.label, color: meta.color, bg: meta.colorBg, Icon: meta.Icon };

    return JINE;
}

/** Týká se notifikace přihlášeného zaměstnance samotného (ne podřízeného)? */
const oMne = (data, user) => !!data.zamestnanec_id
    && !!user?.zamestnanec_id
    && Number(data.zamestnanec_id) === Number(user.zamestnanec_id);

// Cíle starších notifikací (bez `data.cil`) podle modulu
const CILE = {
    // Vlastní onboarding a dokumenty k podpisu řeší zaměstnanec na Přehledu,
    // nadřízený vidí nového kolegu v Zaměstnancích.
    onboarding: (d, user) => (oMne(d, user) || !d.zamestnanec_id)
        ? { page: 'prehled', label: 'Otevřít přehled' }
        : { page: 'zamestnanci', label: 'Zaměstnanci' },
    dokumenty: () => ({ page: 'prehled', label: 'Otevřít přehled' }),
    zamestnanci: () => ({ page: 'zamestnanci', label: 'Zaměstnanci' }),
    skoleni: (d) => d.prubeh_id
        ? { page: 'moje-skoleni-detail', params: { skoleniId: d.prubeh_id }, label: 'Otevřít školení' }
        // Bez průběhu: vlastní školení má zaměstnanec v Cockpitu (Osobní rozvoj),
        // modul Vzdělávání od 2026-09-28 neexistuje.
        : { page: 'prehled', params: { tab: 'rozvoj' }, label: 'Moje vzdělávání' },
    hodnoceni: () => ({ page: 'zamestnanci', params: { tab: 'hodnoceni' }, label: 'Hodnocení' }),
    smeny: () => ({ page: 'smeny', label: 'Směny' }),
    // Směrnici čte a potvrzuje každý ve Znalostech (modul Směrnice zanikl 2026-10-11)
    smernice: (d) => ({ page: 'znalosti-seznam', params: d.smernice_id ? { druh: 'smernice', smernice: d.smernice_id } : { druh: 'smernice' }, label: 'Otevřít směrnici' }),
    podnety: () => ({ page: 'podnety', label: 'Otevřít podněty' }),
    // Manažer schvaluje v Pokladně, řidič vidí výsledek u své směny
    pokladna: (d, user) => muze(user, 'finance.pokladna')
        ? { page: 'pokladna', label: 'Otevřít pokladnu' }
        : { page: 'prehled', label: 'Moje směna' },
    finance: (d) => ({
        page: 'finance',
        params: d.doklad_id ? { tab: 'doklady', dokladId: d.doklad_id } : { tab: 'doklady' },
        label: d.doklad_id ? 'Otevřít doklad' : 'Doklady',
    }),
    // Tankování doplňuje řidič na své směně, alerty vozidel jdou do Rozvozu → Auta
    rozvoz: (d) => d.tankovani_id
        ? { page: 'prehled', label: 'Doplnit na směně' }
        : { page: 'rozvoz', params: { tab: 'auta' }, label: 'Auta' },
    // Měřidla energií jsou od 2026-10 měřicí body v Kontrolách a měření
    energie: (d) => ({ page: 'checklisty', params: { tab: 'parametry', ...(d.parametr_id ? { bod: d.parametr_id } : null) }, label: 'Otevřít měřidlo' }),
    checklisty: () => ({ page: 'checklisty', label: 'Kontroly a měření' }),
    social: () => ({ page: 'social', label: 'Sociální sítě' }),
};

/**
 * Kam vede klik na notifikaci: `{ page, params, label }`, nebo null.
 * Stránku, na kterou uživatel nesmí, nenabízí — skončil by na „Bez přístupu".
 */
export function cilNotifikace(n, user) {
    const cil = n?.data?.cil || CILE[n?.modul]?.(n.data || {}, user);
    if (!cil?.page) return null;

    // Cíl je spočítaný při odeslání a uložený v `data.cil`, takže notifikace
    // z doby, kdy zaměstnanci do modulu Směny ještě směli, míří pořád tam.
    // Bez náhrady by jim karta zůstala bez akce — všechno, co v tom odkazu
    // bylo, mezitím umí Můj měsíc.
    if (!smiNaStranku(user, cil.page)) {
        if (n?.modul === 'smeny' && smiNaStranku(user, 'prehled')) {
            const datum = n.data?.datum || n.data?.od || null;
            return {
                page: 'prehled',
                params: { tab: 'muj-mesic', ...(datum ? { datum } : null) },
                label: 'Otevřít můj měsíc',
            };
        }
        return null;
    }

    // Prázdné params přijdou z PHP jako [] — navigace čeká objekt
    const params = cil.params && !Array.isArray(cil.params) ? cil.params : {};
    return { ...cil, params, label: cil.label || 'Otevřít' };
}

/** Označí notifikaci přečtenou a dá vědět odznakům. */
export function oznacitPrectenou(id) {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
    fetch(`/api/notifikace/${id}/precist`, { method: 'POST', headers: { 'X-CSRF-TOKEN': csrf } })
        .then(() => window.dispatchEvent(new Event('notifikace-changed')))
        .catch(() => {});
}

/**
 * Notifikace, která se vyřizuje **přímo v seznamu**, dokud ji někdo neuzavře.
 *
 * Patří sem rozhodnutí ano/ne, ke kterému není co dostudovat: prohození směny
 * a žádost o nepřítomnost. Podpisy (smlouva, směrnice, dokument) sem schválně
 * nepatří — ty potřebují dokument otevřít, ne odkliknout z notifikace.
 *
 * U nepřítomnosti se navíc ptáme na oprávnění: notifikace sice chodí jen
 * schvalovatelům, ale tlačítko, které skončí na 403, je horší než žádné.
 */
export function jeAkcniNotifikace(n, user = null) {
    if (n?.vyrizeno) return false;
    const typ = n?.data?.typ_akce;
    if (typ === 'prohoz_navrh' || typ === 'prohoz_admin') return true;
    // Od 2026-09-29 rozhoduje o nepřítomnosti i nadřízený bez přístupu do Směn —
    // notifikace chodí jen tomu, kdo rozhoduje, backend to ještě ověří.
    if (typ === 'nepritomnost_zadost') return !!n?.data?.nedostupnost_id;
    if (typ === 'zadost_ke_schvaleni') return !!n?.data?.zadost_id;
    if (typ === 'zaskok_nabidka' || typ === 'zaskok_potvrzeni') return !!n?.data?.zaskok_id;
    return false;
}

/**
 * Vyřízení akční notifikace — vrací `{path, method}` pro dané rozhodnutí,
 * nebo null. Cesty drží na jednom místě obě místa, kde se tlačítka kreslí
 * (zvoneček a stránka Notifikace).
 */
export function cestaRozhodnuti(n, souhlas) {
    const typ = n?.data?.typ_akce;
    if (typ === 'prohoz_navrh') {
        return `/api/shifts/prohozeni/${n.data.navrh_id}/${souhlas ? 'akceptovat' : 'odmitnout'}`;
    }
    if (typ === 'prohoz_admin') {
        return `/api/shifts/prohozeni/${n.data.navrh_id}/${souhlas ? 'admin-schvalit' : 'admin-zamitnout'}`;
    }
    if (typ === 'nepritomnost_zadost') {
        return `/api/shifts/nedostupnosti/${n.data.nedostupnost_id}/${souhlas ? 'schvalit' : 'zamitnout'}`;
    }
    if (typ === 'zadost_ke_schvaleni') {
        return `/api/zadosti/${n.data.zadost_id}/${souhlas ? 'schvalit' : 'zamitnout'}`;
    }
    if (typ === 'zaskok_nabidka') return `/api/zaskoky/${n.data.zaskok_id}/${souhlas ? 'beru' : 'nemuzu'}`;
    if (typ === 'zaskok_potvrzeni') return `/api/zaskoky/${n.data.zaskok_id}/${souhlas ? 'potvrdit' : 'odmitnout-kandidata'}`;
    return null;
}

/**
 * Rozhodnutí, u kterého jde před schválením opravit údaje (třetí tlačítko
 * „Upravit"). Zatím jen nepřítomnost — schvalovatel dopíše správné hodiny.
 * Vrací id nepřítomnosti, nebo null.
 */
export function upravitelnaNepritomnost(n) {
    return n?.data?.typ_akce === 'nepritomnost_zadost' ? (n.data.nedostupnost_id ?? null) : null;
}

/** Popisky tlačítek podle druhu rozhodnutí. */
export function popiskyRozhodnuti(n) {
    if (n?.data?.typ_akce === 'zaskok_nabidka') return { ano: 'Beru', ne: 'Nemůžu' };
    if (n?.data?.typ_akce === 'zaskok_potvrzeni') return { ano: 'Potvrdit', ne: 'Odmítnout' };
    return ['nepritomnost_zadost', 'zadost_ke_schvaleni'].includes(n?.data?.typ_akce)
        ? { ano: 'Schválit', ne: 'Zamítnout' }
        : { ano: 'Akceptovat', ne: 'Odmítnout' };
}

/** Barva závažnosti (info/success/warning/error), null = bez zvýraznění. */
export function barvaTypu(typ) {
    return { warning: '#FF9500', error: '#FF3B30' }[typ] || null;
}
